import type { CSSProperties } from 'react'
import { getMotif } from './motifs'
import type { TemplateFrame, TemplateLayout } from '../../lib/inviteTemplates'

/**
 * Hangs a template's motif around the viewport, the way the original designs
 * hang their cut-out PNGs (TEMPLATE_ART in InvitationPage.tsx): fixed,
 * pointer-less SVGs pinned to the window edges so they frame the content at
 * any aspect ratio instead of scrolling away with it.
 *
 * `frame` chooses the arrangement; every slot is scaled by `--mo-scale`
 * (1 on desktop, smaller on phones — set in InvitationPage.css) and drifts
 * on the `translate` property so a static `transform` can still mirror the
 * right-hand copies.
 */

type Slot = {
  style: CSSProperties
  size: number
  rot?: number
  flip?: boolean
  /** Position is the slot's horizontal centre, not its left edge. */
  center?: boolean
  op?: number
}

const at = (
  pos: { left?: string; right?: string; top?: string; bottom?: string },
  size: number,
  opts: Omit<Slot, 'style' | 'size'> = {},
): Slot => ({ style: pos, size, ...opts })

const FRAMES: Record<TemplateFrame, Slot[]> = {
  none: [],
  corners: [
    at({ left: '1.5vw', top: '1.5vh' }, 170),
    at({ right: '1.5vw', top: '1.5vh' }, 170, { flip: true }),
    at({ left: '1.5vw', bottom: '1vh' }, 170),
    at({ right: '1.5vw', bottom: '1vh' }, 170, { flip: true }),
  ],
  edges: [
    at({ left: '1vw', bottom: '0.5vh' }, 240),
    at({ right: '1vw', bottom: '0.5vh' }, 240, { flip: true }),
    at({ left: '2vw', top: '2vh' }, 110, { op: 0.8 }),
    at({ right: '2vw', top: '2vh' }, 110, { flip: true, op: 0.8 }),
  ],
  garland: [
    ...[8, 22, 36, 50, 64, 78, 92].map((x, i) =>
      at({ left: `${x}%`, top: '0.5vh' }, 84, { rot: i % 2 ? 8 : -8, center: true }),
    ),
    at({ left: '1.5vw', bottom: '1vh' }, 140),
    at({ right: '1.5vw', bottom: '1vh' }, 140, { flip: true }),
  ],
  bottom: [
    ...[10, 26, 42, 58, 74, 90].map((x, i) =>
      at({ left: `${x}%`, bottom: '0' }, 100, { rot: i % 2 ? 4 : -4, center: true }),
    ),
    at({ left: '2vw', top: '2vh' }, 100, { op: 0.8 }),
    at({ right: '2vw', top: '2vh' }, 100, { flip: true, op: 0.8 }),
  ],
  scatter: [
    at({ left: '3vw', top: '10vh' }, 110, { rot: -12 }),
    at({ right: '3vw', top: '6vh' }, 90, { rot: 14, flip: true }),
    at({ left: '1vw', top: '46vh' }, 70, { rot: 8 }),
    at({ right: '2vw', top: '40vh' }, 80, { rot: -10, flip: true }),
    at({ left: '5vw', bottom: '8vh' }, 120, { rot: 6 }),
    at({ right: '5vw', bottom: '10vh' }, 130, { rot: -6, flip: true }),
    at({ left: '30vw', top: '1vh' }, 60, { rot: 20 }),
    at({ right: '30vw', top: '2vh' }, 70, { rot: -18 }),
    at({ left: '24vw', bottom: '1vh' }, 75, { rot: -8 }),
    at({ right: '24vw', bottom: '2vh' }, 65, { rot: 12 }),
  ],
}

type Props = {
  motif: string
  frame: TemplateFrame
  /** The composition family — decides which corners hold information. */
  layout?: TemplateLayout
}

// Corners that carry information get no artwork. The poster family sets the
// date mark bottom-left and the venue + map link bottom-right (and its
// Details button along the bottom edge), so nothing hangs off the bottom
// there; the other families keep their bottom corners free of content.
const freeSlots = (slots: Slot[], layout?: TemplateLayout) =>
  layout === 'poster' ? slots.filter((s) => s.style.bottom === undefined) : slots

const TemplateMotifLayer = ({ motif, frame, layout }: Props) => {
  const { Icon } = getMotif(motif)
  const slots = freeSlots(FRAMES[frame] || [], layout)
  if (!slots.length || motif === 'none') return null
  return (
    <div className="inv-mo-layer" aria-hidden="true">
      {slots.map((s, i) => (
        <svg
          key={i}
          className="inv-mo"
          viewBox="0 0 100 100"
          style={
            {
              ...s.style,
              width: `calc(${s.size}px * var(--mo-scale, 1))`,
              height: `calc(${s.size}px * var(--mo-scale, 1))`,
              opacity: s.op ?? 1,
              transform: `${s.center ? 'translateX(-50%) ' : ''}rotate(${s.rot || 0}deg)${s.flip ? ' scaleX(-1)' : ''}`,
              '--mo-del': `${(i * 0.7) % 5}s`,
              '--mo-dur': `${8 + (i % 4) * 1.5}s`,
            } as CSSProperties
          }
        >
          <Icon />
        </svg>
      ))}
    </div>
  )
}

export default TemplateMotifLayer
